import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Select, Button, Alert, Typography, Row, Col } from 'antd';
import styled from '@emotion/styled';
import MortgageOverpayments from './MortgageOverpayments';

const { Option } = Select;
const { Text, Title } = Typography;

// Months array for month selection
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June', 
  'July', 'August', 'September', 'October', 'November', 'December'
];

const AnimatedFormItem = styled(Form.Item)`
  transition: all 0.3s ease-in-out;
  max-height: ${props => props.visible ? '100px' : '0'};
  opacity: ${props => props.visible ? '1' : '0'};
  overflow: hidden;
  margin-bottom: ${props => props.visible ? '16px' : '0'};
`;

const CalculatorForm = ({
  calculationType,
  currency,
  balance,
  interestRate,
  monthlyContribution,
  additionalSpending,
  annualFeeRate,
  targetMonths,
  targetEndBalance,
  contributionMonths,
  years,
  months,
  // New mortgage-specific props
  propertyValue,
  mortgageAmount,
  deposit,
  fixedRate,
  fixedTerm,
  totalTerm,
  variableRate,
  mortgageType,
  mortgageStartMonth,
  mortgageStartYear,
  // Setters for original props
  setCalculationType,
  setCurrency,
  setMonthlyContribution,
  setBalance,
  setInterestRate,
  setAdditionalSpending,
  setTargetMonths,
  setContributionMonths,
  setTargetEndBalance,
  setYears,
  setMonths,
  setAnnualFeeRate,
  // New mortgage-specific setters
  setPropertyValue,
  setMortgageAmount,
  setDeposit,
  setFixedRate,
  setFixedTerm,
  setTotalTerm,
  setVariableRate,
  setMortgageType,
  setMortgageStartMonth,
  setMortgageStartYear,
  monthlyOverpayment = 0,
  lumpSumOverpayments = [],
  overpaymentStrategy = 'reduce-term',
  setMonthlyOverpayment,
  setLumpSumOverpayments,
  setOverpaymentStrategy,
  // Other props
  errorMessage,
  inputsChanged,
  setInputsChanged,
  calculatePayments,
  handleReset
}) => {
  const [investmentOption, setInvestmentOption] = useState('monthly');
  const [showInputs, setShowInputs] = useState(false);

  // Generate years array (current year +/- 10 years)
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({length: 21}, (_, i) => currentYear - 10 + i);

  useEffect(() => {
    if (calculationType === 'investment') {
      // Reset fields when switching between investment options
      setMonthlyContribution('');
      setTargetEndBalance('');
      setContributionMonths('');
      setYears('');
      setMonths('');

      // Trigger animation
      setShowInputs(false);
      setTimeout(() => setShowInputs(true), 50);
    }
  }, [investmentOption, calculationType]);
  
  const handleYearChange = (value) => {
    setYears(value);
    setInputsChanged(true);
  };

  const handleMonthChange = (value) => {
    setMonths(value);
    setInputsChanged(true);
  };    

  const formItemStyle = {
    marginBottom: '20px'
  };

  const inputStyle = {
    width: '100%'
  };

  const sectionTitleStyle = {
    marginBottom: '12px',
    marginTop: '12px'
  };

  const getCurrencySymbol = () => {
    switch (currency) {
      case 'GBP': return '£';
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return '';
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '';
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Render mortgage-specific inputs
  const renderMortgageInputs = () => (
    <>
      <Title level={4} style={sectionTitleStyle}>Mortgage Details</Title>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Property Value"
            required
            tooltip="Enter the total value of the property"
            style={formItemStyle}
          >
            <InputNumber
              value={propertyValue}
              onChange={(value) => {
                setPropertyValue(value);
    
                // If mortgage amount is not set, calculate it based on property value and deposit
                if (!mortgageAmount && deposit) {
                  setMortgageAmount(value - deposit);
                }
                
                setInputsChanged(true);
              }}
              placeholder="Enter property value"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={1000}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Deposit/Equity"
            tooltip="Enter your deposit amount (optional) or the value of your current equity in the property"
            style={formItemStyle}
          >
            <InputNumber
              value={deposit}
              onChange={(value) => {
                setDeposit(value);
    
                // If property value is set, calculate mortgage amount
                if (propertyValue) {
                  setMortgageAmount(propertyValue - value);
                }
                
                setInputsChanged(true);
              }}
              placeholder="Enter deposit amount"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={1000}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Mortgage Amount"
            tooltip="Enter the specific mortgage loan amount (optional)"
            style={formItemStyle}
          >
            <InputNumber
              value={mortgageAmount}
              onChange={(value) => {
                setMortgageAmount(value);
    
                // If property value is set, calculate deposit
                if (propertyValue) {
                  setDeposit(propertyValue - value);
                }
                
                setInputsChanged(true);
              }}
              placeholder="Enter mortgage amount"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={1000}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>

      <Title level={4} style={sectionTitleStyle}>Mortgage Start Date</Title>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Start Month"
            required
            style={formItemStyle}
          >
            <Select
              value={mortgageStartMonth}
              onChange={(value) => {
                setMortgageStartMonth(value);
                setInputsChanged(true);
              }}
              style={inputStyle}
            >
              {MONTHS.map((month, index) => (
                <Option key={index} value={index}>{month}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Start Year"
            required
            style={formItemStyle}
          >
            <Select
              value={mortgageStartYear}
              onChange={(value) => {
                setMortgageStartYear(value);
                setInputsChanged(true);
              }}
              style={inputStyle}
            >
              {YEARS.map((year) => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Title level={4} style={sectionTitleStyle}>Mortgage Terms</Title>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Initial Fixed Rate (%)"
            required
            tooltip="Enter the initial fixed interest rate"
            style={formItemStyle}
          >
            <InputNumber
              value={fixedRate}
              onChange={(value) => {
                setFixedRate(value);
                setInputsChanged(true);
              }}
              placeholder="Enter fixed rate"
              style={inputStyle}
              suffix="%"
              precision={2}
              step={0.1}
              min={0}
              max={100}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Fixed Rate Term (years)"
            required
            tooltip="Enter how long the fixed rate period lasts"
            style={formItemStyle}
          >
            <InputNumber
              value={fixedTerm}
              onChange={(value) => {
                setFixedTerm(value);
                setInputsChanged(true);
              }}
              placeholder="Enter fixed term"
              style={inputStyle}
              precision={0}
              step={1}
              min={1}
              max={totalTerm}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Total Mortgage Term (years)"
            required
            tooltip="Enter the total length of the mortgage, this includes the fixed rate term (double counting)"
            style={formItemStyle}
          >
            <InputNumber
              value={totalTerm}
              onChange={(value) => {
                setTotalTerm(value);
                setInputsChanged(true);
              }}
              placeholder="Enter total term"
              style={inputStyle}
              precision={0}
              step={1}
              min={1}
              max={40}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Variable Rate After Fixed Term (%)"
            required
            tooltip="Enter the expected variable rate after the fixed period"
            style={formItemStyle}
          >
            <InputNumber
              value={variableRate}
              onChange={(value) => {
                setVariableRate(value);
                setInputsChanged(true);
              }}
              placeholder="Enter variable rate"
              style={inputStyle}
              suffix="%"
              precision={2}
              step={0.1}
              min={0}
              max={100}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Mortgage Type"
            required
            tooltip="Select the type of mortgage"
            style={formItemStyle}
          >
            <Select
              value={mortgageType}
              onChange={(value) => {
                setMortgageType(value);
                setInputsChanged(true);
              }}
              style={inputStyle}
            >
              <Option value="repayment">Repayment</Option>
              <Option value="interest-only">Interest Only</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {mortgageType === 'repayment' && (
        <>
          <Title level={4} style={sectionTitleStyle}>Overpayments</Title>
          <Row gutter={[24, 0]}>
            <Col xs={24}>
              <Form.Item 
                label="Overpayment Strategy"
                tooltip="Choose how overpayments should be applied"
                style={formItemStyle}
              >
                <Select
                  value={overpaymentStrategy}
                  onChange={(value) => {
                    setOverpaymentStrategy(value);
                    setInputsChanged(true);
                  }}
                  style={inputStyle}
                >
                  <Option value="reduce-term">Reduce mortgage term</Option>
                  <Option value="reduce-payment">Reduce monthly payments</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <MortgageOverpayments
            monthlyOverpayment={monthlyOverpayment}
            lumpSumOverpayments={lumpSumOverpayments}
            onMonthlyOverpaymentChange={(value) => {
              setMonthlyOverpayment(value);
              setInputsChanged(true);
            }}
            onLumpSumOverpaymentAdd={(payment) => {
              setLumpSumOverpayments([...(lumpSumOverpayments || []), payment]);
              setInputsChanged(true);
            }}
            onLumpSumOverpaymentRemove={(key) => {
              setLumpSumOverpayments((lumpSumOverpayments || []).filter(p => p.key !== key));
              setInputsChanged(true);
            }}
            remainingBalance={mortgageAmount}
            startDate={new Date(mortgageStartYear, mortgageStartMonth)}
          />
        </>
      )}
    </>
  );

  const renderDebtInputs = () => (
    <>
      <Title level={4} style={sectionTitleStyle}>Debt Details</Title>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Current Debt Balance"
            required
            tooltip="Examples; amount of credit card debt, total amount of loan, etc."
            style={formItemStyle}
          >
            <InputNumber
              value={balance}
              onChange={(value) => {
                setBalance(value);
                setInputsChanged(true);
              }}
              placeholder="Enter the balance of debt"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={100}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Annual Interest Rate (%)"
            required
            tooltip="The rate of interest assigned to your debt on an annualized basis"
            style={formItemStyle}
          >
            <InputNumber
              value={interestRate}
              onChange={(value) => {
                setInterestRate(value);
                setInputsChanged(true);
              }}
              placeholder="Enter annual interest rate"
              style={inputStyle}
              suffix="%"
              precision={2}
              step={0.1}
              min={0}
              max={100}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Additional Monthly Spending"
            tooltip="If you expect to spend more money using this debt method (e.g. a credit card) going forwards"
            style={formItemStyle}
          >
            <InputNumber
              value={additionalSpending}
              onChange={(value) => {
                setAdditionalSpending(value);
                setInputsChanged(true);
              }}
              placeholder="Enter additional monthly spending"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={10}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Monthly Payment"
            tooltip="How much you aim to pay the debt off by on a monthly basis"
            style={formItemStyle}
          >
            <InputNumber
              value={monthlyContribution}
              onChange={(value) => {
                setMonthlyContribution(value);
                setInputsChanged(true);
              }}
              placeholder="Enter monthly payment"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={10}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Target Months to Pay Off"
            tooltip="Instead of Monthly Payment, you can set a target number of months to pay off the debt to understand what monthly payment would be required."
            style={formItemStyle}
          >
            <InputNumber
              value={targetMonths}
              onChange={(value) => {
                setTargetMonths(value);
                setInputsChanged(true);
              }}
              placeholder="Number of months"
              style={inputStyle}
              precision={0}
              step={1}
              min={1}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // Render Investment specific inputs
  const renderInvestmentInputs = () => (
    <>
      <Title level={4} style={sectionTitleStyle}>Investment Details</Title>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Initial Investment"
            tooltip="If starting from 0, enter 0."
            style={formItemStyle}
          >
            <InputNumber
              value={balance}
              onChange={(value) => {
                setBalance(value);
                setInputsChanged(true);
              }}
              placeholder="Enter initial investment"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={100}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Annual Growth Rate (%)"
            required
            tooltip="Enter the expected % growth rate each year"
            style={formItemStyle}
          >
            <InputNumber
              value={interestRate}
              onChange={(value) => {
                setInterestRate(value);
                setInputsChanged(true);
              }}
              placeholder="Enter annual growth rate"
              style={inputStyle}
              suffix="%"
              precision={2}
              step={0.1}
              min={0}
              max={100}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Annual Fee Rate (%)"
            tooltip="Some investment providers charge a % annual fee, enter it here if applicable"
            style={formItemStyle}
          >
            <InputNumber
              value={annualFeeRate}
              onChange={(value) => {
                setAnnualFeeRate(value);
                setInputsChanged(true);
              }}
              placeholder="Enter annual fee rate"
              style={inputStyle}
              suffix="%"
              precision={2}
              step={0.1}
              min={0}
              max={100}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Monthly Investment"
            tooltip="Enter the monthly amount you expect to contribute towards your investments going forwards"
            style={formItemStyle}
          >
            <InputNumber
              value={monthlyContribution}
              onChange={(value) => {
                setMonthlyContribution(value);
                setInputsChanged(true);
              }}
              placeholder="Enter monthly contribution"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={10}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Contribution Duration (months)"
            tooltip="How long do you expect to contribute towards your investments for, at this monthly amount?"
            style={formItemStyle}
          >
            <InputNumber
              value={contributionMonths}
              onChange={(value) => {
                setContributionMonths(value);
                setInputsChanged(true);
              }}
              placeholder="Contribution months"
              style={inputStyle}
              precision={0}
              step={1}
              min={1}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Target End Balance"
            tooltip="If instead you have a target end balance in mind, enter it here. Use Investment Years/Months to set the time horizon at which point you want this target amount"
            style={formItemStyle}
          >
            <InputNumber
              value={targetEndBalance}
              onChange={(value) => {
                setTargetEndBalance(value);
                setInputsChanged(true);
              }}
              placeholder="Enter target balance"
              style={inputStyle}
              prefix={getCurrencySymbol()}
              formatter={formatNumber}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              precision={2}
              step={100}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Investment Years"
            tooltip="What is the time horizon for your investment?"
            style={formItemStyle}
          >
            <InputNumber
              value={years}
              onChange={(value) => {
                setYears(value);
                setInputsChanged(true);
              }}
              placeholder="Years"
              style={inputStyle}
              precision={0}
              step={1}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="and Investment Months"
            style={formItemStyle}
          >
            <InputNumber
              value={months}
              onChange={(value) => {
                setMonths(value);
                setInputsChanged(true);
              }}
              placeholder="Additional months"
              style={inputStyle}
              precision={0}
              step={1}
              min={0}
              max={11}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  ); 

  return (
    <Form layout="vertical" style={{ maxWidth: '100%', margin: '0 auto', padding: '0 20px' }}>
      <Title level={4} style={sectionTitleStyle}>Account Type and Currency</Title>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Type"
            required
            tooltip="Select calculation type"
            style={formItemStyle}
          >
            <Select 
              value={calculationType} 
              onChange={setCalculationType}
              style={inputStyle}
            >
              <Option value="debt">Debt</Option>
              <Option value="investment">Investment</Option>
              <Option value="mortgage">Mortgage</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item 
            label="Currency"
            required
            style={formItemStyle}
          >
            <Select
              value={currency}
              onChange={setCurrency}
              style={inputStyle}
            >
              <Option value="GBP">£ (GBP)</Option>
              <Option value="USD">$ (USD)</Option>
              <Option value="EUR">€ (EUR)</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* Render different calculator forms based on type */}
      {calculationType === 'mortgage' && renderMortgageInputs()}
      {calculationType === 'debt' && renderDebtInputs()}
      {calculationType === 'investment' && renderInvestmentInputs()}
      
      {/* Rest of the existing form remains the same */}
      {/* (Keep the existing debt and investment input sections) */}

      {errorMessage && (
        <Form.Item style={formItemStyle}>
          <Alert message={errorMessage} type="error" showIcon />
        </Form.Item>
      )}

      {inputsChanged && (
        <Form.Item style={formItemStyle}>
          <Alert 
            message="Inputs have changed. Please click 'Calculate' to update the results." 
            type="warning" 
            showIcon 
          />
        </Form.Item>
      )}

      <Form.Item style={{ ...formItemStyle, marginTop: '24px' }}>
        <Button type="primary" onClick={calculatePayments} style={{ marginRight: '10px' }}>
          Calculate
        </Button>
        <Button onClick={handleReset}>
          Reset
        </Button>
      </Form.Item>
    </Form>
  );
};
  
export default CalculatorForm;